import { MATRIX_PIVOTS, type MatrixSummary } from '@mss_creator/shared';
import {
  ALL_FOLDERS,
  type FolderSelection,
  MATRIX_DISPLAY_STATES,
  type MatrixGridSettings,
  type MatrixGridSort,
  NO_FOLDER,
  type SortDirection,
  destinataireFilterValue,
  matrixDisplayState,
} from './matrix-grid-search';

/**
 * Client-side filtering and sorting of a company's matrix summaries (issue
 * #16, règle complémentaire 7: a few dozen matrices per company, no
 * pagination). The server returns them `updatedAt` desc; the grid's default
 * order (creation date, newest first) is applied here.
 */

export const UNTITLED_MATRIX_LABEL = 'Sans titre';

/** What a card or a row shows as the title. */
export function matrixTitle(matrix: Pick<MatrixSummary, 'sujet'>): string {
  const sujet = matrix.sujet?.trim() ?? '';
  return sujet === '' ? UNTITLED_MATRIX_LABEL : sujet;
}

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** « 28 sept. 2026 », the card footer's and the list's date. */
export function formatMatrixDate(iso: string): string {
  return DATE_FORMAT.format(new Date(iso));
}

/** Where a card or a row leads: the matrix page once generated, the wizard otherwise. */
export function matrixLinkTarget(
  matrix: Pick<MatrixSummary, 'state'>,
): '/matrices/$matrixId' | '/wizard/$matrixId' {
  return matrix.state === 'généré' ? '/matrices/$matrixId' : '/wizard/$matrixId';
}

/** Case- and accent-insensitive form used by the text search. */
export function foldForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('fr');
}

/** The folder view (`dossier`) is navigation, not a filter: it never counts here. */
export function hasActiveFilters(settings: MatrixGridSettings): boolean {
  return (
    settings.q.trim() !== '' ||
    settings.states.length > 0 ||
    settings.pivots.length > 0 ||
    settings.destinataires.length > 0 ||
    settings.createdBy !== 'all'
  );
}

/** « Réinitialiser »: the filters only; the sort, the view and the folder stay. */
export function clearFilters(settings: MatrixGridSettings): MatrixGridSettings {
  return { ...settings, q: '', states: [], pivots: [], destinataires: [], createdBy: 'all' };
}

/** Whether `matrix` belongs to the chosen « dossier » view (« Toutes », « Hors dossier » or one folder). */
export function matchesFolder(
  matrix: Pick<MatrixSummary, 'folderId'>,
  dossier: FolderSelection,
): boolean {
  if (dossier === ALL_FOLDERS) return true;
  if (dossier === NO_FOLDER) return matrix.folderId === null;
  return matrix.folderId === dossier;
}

/** `currentUserId` is undefined while the user loads: « Moi » then matches nothing yet. */
export function matchesFilters(
  matrix: MatrixSummary,
  settings: MatrixGridSettings,
  currentUserId: string | undefined,
): boolean {
  if (!matchesFolder(matrix, settings.dossier)) return false;
  const needle = foldForSearch(settings.q.trim());
  if (needle !== '' && !foldForSearch(matrix.sujet ?? '').includes(needle)) return false;
  if (settings.states.length > 0 && !settings.states.includes(matrixDisplayState(matrix))) {
    return false;
  }
  if (settings.pivots.length > 0 && !settings.pivots.includes(matrix.pivot)) return false;
  if (
    settings.destinataires.length > 0 &&
    !settings.destinataires.includes(destinataireFilterValue(matrix))
  ) {
    return false;
  }
  if (settings.createdBy === 'me') {
    return currentUserId !== undefined && matrix.createdBy?.id === currentUserId;
  }
  return true;
}

const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

function compareBy(sort: MatrixGridSort, a: MatrixSummary, b: MatrixSummary): number {
  switch (sort) {
    case 'created':
      return Date.parse(a.createdAt) - Date.parse(b.createdAt);
    case 'updated':
      return Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
    case 'sujet':
      return collator.compare(matrixTitle(a), matrixTitle(b));
    case 'state':
      return (
        MATRIX_DISPLAY_STATES.indexOf(matrixDisplayState(a)) -
        MATRIX_DISPLAY_STATES.indexOf(matrixDisplayState(b))
      );
    case 'pivot':
      return MATRIX_PIVOTS.indexOf(a.pivot) - MATRIX_PIVOTS.indexOf(b.pivot);
  }
}

/**
 * Sorted copy. Ties (same state, same pivot, same sujet) fall back to the
 * newest creation first whatever the direction, so the order is stable.
 */
export function sortMatrices(
  matrices: readonly MatrixSummary[],
  sort: MatrixGridSort,
  dir: SortDirection,
): MatrixSummary[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...matrices].sort((a, b) => {
    const primary = compareBy(sort, a, b) * sign;
    if (primary !== 0) return primary;
    const byCreation = compareBy('created', b, a);
    return byCreation !== 0 ? byCreation : a.id.localeCompare(b.id);
  });
}

/** Filtered, then sorted: what the grid or the list shows. */
export function visibleMatrices(
  matrices: readonly MatrixSummary[],
  settings: MatrixGridSettings,
  currentUserId: string | undefined,
): MatrixSummary[] {
  return sortMatrices(
    matrices.filter((matrix) => matchesFilters(matrix, settings, currentUserId)),
    settings.sort,
    settings.dir,
  );
}
